import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useCustomStore } from '../../../hooks';
import { TrainingType } from '../../../types';
import { DATE_PICKER_DATE_FORMAT } from '../../../utils/helper';
import { SnackBarConfig } from '../../../utils/SnackBarConfig';
import { SelectDatePicker } from '../../Forms/datePicker';
import { InputText } from '../../Forms/InputText';
import { SelectInput } from '../../Forms/selectInput';
import search from './../../../assets/images/h-search.svg';

type SearchFormProps = {
  classes: any;
  onClose: () => void;
};

type SearchFormType = {
  contactId: string;
  contactName: string;
  videoAssigned: string;
  reason: string;
  status: string;
  assignedBy: string;
  assignedById: string;
  startDateStart: string;
  startDateEnd: string;
  endDateStart: string;
  endDateEnd: string;
  trainingType?: TrainingType;
  inspectionReportNumber?: string;
  accidentNumber?: string;
};

const TrainingCompletedSearchForm: React.FC<any> = (props: SearchFormProps) => {
  const { classes, onClose } = props;
  const { trainingStore, authStore } = useCustomStore();
  const { enqueueSnackbar } = useSnackbar();

  const [statuses, setStatuses] = useState<any[]>([]);
  const [reasons, setReasons] = useState<any[]>([]);
  const [assignees, setAssignees] = useState<any[]>([]);

  // Training drop down options list
  const trainingTypeOptions = [
    {
      label: 'All',
      optionClassName: classes.optionsClassName,
      value: TrainingType.ALL,
    },
    {
      label: 'Document-based',
      optionClassName: classes.optionsClassName,
      value: TrainingType.DOCUMENT,
    },
    {
      label: 'Video-based',
      optionClassName: classes.optionsClassName,
      value: TrainingType.VIDEO,
    },
  ];

  const { control, handleSubmit, setValue, watch } = useForm<SearchFormType>({
    defaultValues: {
      accidentNumber: trainingStore.TrainingCompletedFilters.accidentNumber,
      assignedBy: trainingStore.TrainingCompletedFilters.assignedBy,
      assignedById: trainingStore.TrainingCompletedFilters.assignedById,
      contactId: trainingStore.TrainingCompletedFilters.contactId,
      contactName: trainingStore.TrainingCompletedFilters.contactName,
      endDateEnd: trainingStore.TrainingCompletedFilters.endDateEnd,
      endDateStart: trainingStore.TrainingCompletedFilters.endDateStart,
      inspectionReportNumber:
        trainingStore.TrainingCompletedFilters.inspectionReportNumber,
      reason: '',
      startDateEnd: trainingStore.TrainingCompletedFilters.startDateEnd,
      startDateStart: trainingStore.TrainingCompletedFilters.startDateStart,
      status: '',
      trainingType: trainingStore.TrainingCompletedFilters.trainingType,
      videoAssigned: trainingStore.TrainingCompletedFilters.videoAssigned,
    },
  });

  const getTrainingAssignmentStatuses = useCallback(async () => {
    if (trainingStore.TrainingAssignmentStatusesData.length > 0) {
      setStatuses(trainingStore.TrainingCompletedAssignmentStatusesData);
      setValue('status', trainingStore.TrainingCompletedFilters.status || '');
    } else {
      trainingStore.setTrainingAssignmentStatusesLoader(true);
      const resp = await trainingStore.getTrainingAssignmentStatuses();
      if (resp.isErr()) {
        enqueueSnackbar(
          'Unable to fetch Training Assignment Statuses',
          SnackBarConfig('e'),
        );
      } else {
        setStatuses(trainingStore.TrainingCompletedAssignmentStatusesData);
        setValue('status', trainingStore.TrainingCompletedFilters.status || '');
      }
      trainingStore.setTrainingAssignmentStatusesLoader(false);
    }
  }, [enqueueSnackbar, setValue, trainingStore]);

  const getTrainingAssignmentReasons = useCallback(async () => {
    trainingStore.setTrainingAssignmentReasonsLoader(true);
    const resp = await trainingStore.getTrainingAssignmentReasons({
      showSystemReason: true,
    });
    if (resp.isErr()) {
      enqueueSnackbar(
        'Unable to fetch Training Assignment Reasons',
        SnackBarConfig('e'),
      );
    } else {
      setReasons(trainingStore.TrainingAssignmentReasonsData);
      setValue('reason', trainingStore.TrainingCompletedFilters.reason || '');
    }
    trainingStore.setTrainingAssignmentReasonsLoader(false);
  }, [enqueueSnackbar, setValue, trainingStore]);

  const getAllTrainingAssignees = useCallback(async () => {
    if (!authStore.IsCompanyAdminLogin) {
      return;
    }
    if (trainingStore.TrainingAssigneesData.length > 0) {
      setAssignees(trainingStore.TrainingAssigneesData);
      setValue(
        'contactId',
        trainingStore.TrainingCompletedFilters.contactId || '',
      );
      setValue(
        'assignedById',
        trainingStore.TrainingCompletedFilters.assignedById || '',
      );
    } else {
      trainingStore.setTrainingAssigneesLoader(true);
      const resp = await trainingStore.getAllUsersForTraining({
        limit: '5000',
        searchText: '',
        userType: 'backOfficePersonnel',
      });
      if (resp.isOk()) {
        const { response } = resp.value;
        setAssignees(response);
        trainingStore.setTrainingAssigneesData(response);
      } else {
        enqueueSnackbar(
          'Unable to fetch Training Assignees',
          SnackBarConfig('e'),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, setValue, trainingStore]);

  useEffect(() => {
    getTrainingAssignmentStatuses();
    getTrainingAssignmentReasons();
    getAllTrainingAssignees();
  }, [
    getTrainingAssignmentStatuses,
    getTrainingAssignmentReasons,
    getAllTrainingAssignees,
    trainingStore,
  ]);

  const onFiltersSubmit = useCallback(
    async (values) => {
      trainingStore.setTrainingCompletedFilters(values);
      trainingStore.setTrainingCompletedLoader(true);
      const resp = await trainingStore.getInitialTrainingCompleted();
      if (resp.isErr()) {
        enqueueSnackbar(
          'Unable to fetch Training Completed Assignments',
          SnackBarConfig('e'),
        );
      }
      trainingStore.setTrainingCompletedLoader(false);
      onClose();
    },
    [enqueueSnackbar, onClose, trainingStore],
  );

  const resetFilters = useCallback(async () => {
    trainingStore.resetTrainingCompletedFilters();
    trainingStore.setTrainingCompletedLoader(true);
    const resp = await trainingStore.getInitialTrainingCompleted();
    if (resp.isErr()) {
      enqueueSnackbar(
        'Unable to fetch Training Completed Assignments',
        SnackBarConfig('e'),
      );
    }
    trainingStore.setTrainingCompletedLoader(false);
    onClose();
  }, [enqueueSnackbar, onClose, trainingStore]);

  return (
    <form
      id="training-completed-search-form"
      onSubmit={handleSubmit(onFiltersSubmit)}>
      {authStore.IsCompanyAdminLogin && (
        <FormControl fullWidth>
          <Typography component={'h3'} className={classes.filterLabel}>
            Assignee Name
          </Typography>
          <Grid className={classes.borderBottom}>
            <Grid container className={classes.searchBox}>
              <Grid item md={2} className={classes.dFlexJA}>
                <img className={classes.filterIcon} src={search} alt="search" />
              </Grid>
              <Grid item md={10}>
                <Controller
                  control={control}
                  defaultValue=""
                  name="contactId"
                  render={({
                    field: { onChange, value, name, ref },
                    formState,
                  }) => {
                    return (
                      <SelectInput
                        name={name}
                        isRefreshValue={true}
                        value={value}
                        size={'small'}
                        onChangeText={onChange}
                        isEditable={assignees.length === 0}
                        selectClassName={classes.selectClassName}
                        placeHolder={'Select'}
                        optionsList={assignees.map((r) => ({
                          label: r.name,
                          optionClassName: classes.optionsClassName,
                          value: r.id,
                        }))}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </FormControl>
      )}
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Training Assigned
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searchBox}>
            <Grid item md={2} className={classes.dFlexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue=""
                name="videoAssigned"
                render={({
                  field: { onChange, value, name, ref },
                  formState,
                }) => {
                  return (
                    <InputText
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      placeholder="Search"
                      onChangeText={onChange}
                      isEditable={false}
                      className={classes.inputF}
                      type={'text'}
                      variant="outlined"
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Reason
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searchBox}>
            <Grid item md={2} className={classes.dFlexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue=""
                name="reason"
                render={({
                  field: { onChange, value, name, ref },
                  formState,
                }) => {
                  return (
                    <SelectInput
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      size={'small'}
                      onChangeText={onChange}
                      isEditable={reasons.length === 0}
                      selectClassName={classes.selectClassName}
                      placeHolder={'Select'}
                      optionsList={reasons.map((r) => ({
                        label: r.name,
                        optionClassName: classes.optionsClassName,
                        value: r.id,
                      }))}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Status
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searchBox}>
            <Grid item md={2} className={classes.dFlexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue=""
                name="status"
                render={({
                  field: { onChange, value, name, ref },
                  formState,
                }) => {
                  return (
                    <SelectInput
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      size={'small'}
                      onChangeText={onChange}
                      isEditable={statuses.length === 0}
                      selectClassName={classes.selectClassName}
                      placeHolder={'Select'}
                      optionsList={statuses.map((s) => ({
                        label: s.name,
                        optionClassName: classes.optionsClassName,
                        value: s.id,
                      }))}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Type
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searchBox}>
            <Grid item md={2} className={classes.dFlexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                name="trainingType"
                defaultValue={TrainingType.ALL}
                render={({ field: { onChange, value, name } }) => {
                  return (
                    <SelectInput
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      size={'small'}
                      onChangeText={onChange}
                      isEditable={false}
                      selectClassName={classes.selectClassName}
                      placeHolder={'Select'}
                      optionsList={trainingTypeOptions}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      {authStore.IsCompanyAdminLogin && (
        <FormControl fullWidth>
          <Typography component={'h3'} className={classes.filterLabel}>
            Assigned By
          </Typography>
          <Grid className={classes.borderBottom}>
            <Grid container className={classes.searchBox}>
              <Grid item md={2} className={classes.dFlexJA}>
                <img className={classes.filterIcon} src={search} alt="search" />
              </Grid>
              <Grid item md={10}>
                <Controller
                  control={control}
                  defaultValue=""
                  name="assignedById"
                  render={({
                    field: { onChange, value, name, ref },
                    formState,
                  }) => {
                    return (
                      <SelectInput
                        name={name}
                        isRefreshValue={true}
                        value={value}
                        size={'small'}
                        onChangeText={onChange}
                        isEditable={assignees.length === 0}
                        selectClassName={classes.selectClassName}
                        placeHolder={'Select'}
                        optionsList={assignees.map((r) => ({
                          label: r.name,
                          optionClassName: classes.optionsClassName,
                          value: r.id,
                        }))}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </FormControl>
      )}
      <Typography component={'h3'} className={classes.filterLabel}>
        Start Date
      </Typography>
      <div className={clsx(classes.borderBottom, classes.dateFilter)}>
        <FormControl className="date-control">
          <Typography component={'h6'} className="label">
            From:
          </Typography>
          <Grid>
            <Grid container className={classes.searchBox}>
              <Grid item md={10} className="date-picker">
                <Controller
                  control={control}
                  name="startDateStart"
                  render={({
                    field: { onChange, value, name, ref },
                    formState,
                  }) => {
                    return (
                      <SelectDatePicker
                        value={value}
                        isRefreshValue
                        inputRefObj={ref}
                        name={name}
                        onChangeDate={(d) => {
                          const formattedValue = moment(d).format(
                            DATE_PICKER_DATE_FORMAT,
                          );
                          onChange(formattedValue);
                        }}
                        className={classes.selectClassName}
                        maxDate={
                          watch('startDateEnd')
                            ? new Date(watch('startDateEnd'))
                            : undefined
                        }
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </FormControl>
        <FormControl className="date-control">
          <Typography component={'h6'} className="label">
            To:
          </Typography>
          <Grid>
            <Grid container className={classes.searchBox}>
              <Grid item md={10} className="date-picker">
                <Controller
                  control={control}
                  name="startDateEnd"
                  render={({
                    field: { onChange, value, name, ref },
                    formState,
                  }) => {
                    return (
                      <SelectDatePicker
                        value={value}
                        isRefreshValue
                        inputRefObj={ref}
                        name={name}
                        onChangeDate={(d) => {
                          const formattedValue = moment(d).format(
                            DATE_PICKER_DATE_FORMAT,
                          );
                          onChange(formattedValue);
                        }}
                        className={classes.selectClassName}
                        minDate={
                          watch('startDateStart')
                            ? new Date(watch('startDateStart'))
                            : undefined
                        }
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </FormControl>
      </div>
      <Typography component={'h3'} className={classes.filterLabel}>
        End Date
      </Typography>
      <div className={clsx(classes.borderBottom, classes.dateFilter)}>
        <FormControl className="date-control">
          <Typography component={'h6'} className="label">
            From:
          </Typography>
          <Grid>
            <Grid container className={classes.searchBox}>
              <Grid item md={10} className="date-picker">
                <Controller
                  control={control}
                  name="endDateStart"
                  render={({
                    field: { onChange, value, name, ref },
                    formState,
                  }) => {
                    return (
                      <SelectDatePicker
                        value={value}
                        isRefreshValue
                        inputRefObj={ref}
                        name={name}
                        onChangeDate={(d) => {
                          const formattedValue = moment(d).format(
                            DATE_PICKER_DATE_FORMAT,
                          );
                          onChange(formattedValue);
                        }}
                        className={classes.selectClassName}
                        maxDate={
                          watch('endDateEnd')
                            ? new Date(watch('endDateEnd'))
                            : undefined
                        }
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </FormControl>
        <FormControl className="date-control">
          <Typography component={'h6'} className="label">
            To:
          </Typography>
          <Grid>
            <Grid container className={classes.searchBox}>
              <Grid item md={10} className="date-picker">
                <Controller
                  control={control}
                  name="endDateEnd"
                  render={({
                    field: { onChange, value, name, ref },
                    formState,
                  }) => {
                    return (
                      <SelectDatePicker
                        value={value}
                        isRefreshValue
                        inputRefObj={ref}
                        name={name}
                        onChangeDate={(d) => {
                          const formattedValue = moment(d).format(
                            DATE_PICKER_DATE_FORMAT,
                          );
                          onChange(formattedValue);
                        }}
                        className={classes.selectClassName}
                        minDate={
                          watch('endDateStart')
                            ? new Date(watch('endDateStart'))
                            : undefined
                        }
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </FormControl>
      </div>
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Report Number
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searchBox}>
            <Grid item md={2} className={classes.dFlexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue=""
                name="inspectionReportNumber"
                render={({
                  field: { onChange, value, name, ref },
                  formState,
                }) => {
                  return (
                    <InputText
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      placeholder="Search"
                      onChangeText={onChange}
                      isEditable={false}
                      className={classes.inputF}
                      type={'text'}
                      variant="outlined"
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Accident Number
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searchBox}>
            <Grid item md={2} className={classes.dFlexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue=""
                name="accidentNumber"
                render={({
                  field: { onChange, value, name, ref },
                  formState,
                }) => {
                  return (
                    <InputText
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      placeholder="Search"
                      onChangeText={onChange}
                      isEditable={false}
                      className={classes.inputF}
                      type={'text'}
                      variant="outlined"
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <Grid container style={{ padding: '0px !important' }}>
        <Grid item md={6}>
          <Typography component={'div'} className={classes.resetF}>
            <Button
              variant="contained"
              className={' d-flex-ja '.concat(classes.resetF)}
              type="button"
              onClick={resetFilters}
              disabled={trainingStore.TrainingCompletedLoader}>
              RESET
            </Button>
          </Typography>
        </Grid>
        <Grid item md={6}>
          <Typography
            component={'button'}
            className={classes.filterBtn}
            type="submit"
            disabled={trainingStore.TrainingCompletedLoader}>
            SEARCH
          </Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default observer(TrainingCompletedSearchForm);
